import { AfterViewInit, Component, Input } from '@angular/core';

import { IObjectStringKeyMap } from '@shared/models';
import { DateRangePickerComponent } from '@shared/components';
import { CustomFormComponent } from '@services/formBuilder/abstract-custom-form-field';
import { PropertyCategory, PropertySource, PropertyStyle } from '@services/property/property-model.interfaces';
import { FormBuilderService, FormField, ILocation, UserdataService, UserService, ZoneService } from '@services';

import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep, each, find, get, includes, last, reject, split } from 'lodash';

@Component({
  selector: 'app-default-value',
  templateUrl: './default-value.component.html',
  styleUrls: ['./default-value.component.scss'],
})
export class DefaultValueComponent extends CustomFormComponent implements AfterViewInit {
  @Input() propertyCategory: PropertyCategory;
  @Input() propertyStyle: PropertyStyle;
  @Input() propertySource: PropertySource;

  private formPrefix = `defaultValue_${uuidv4()}`;
  public formId = `${this.formPrefix}Form`;
  private fields: IObjectStringKeyMap<FormField[]> = {
    [PropertyCategory.Numeric]: [
      {
        inputtype: 'verbatim',
        name: 'value',
        title: 'PROPERTY.Default_Value',
        type: 'number',
        size: 20,
        onChange: () => this.onChange()
      }
    ],
    [PropertyCategory.Text]: [
      {
        inputtype: 'verbatim',
        name: 'value',
        title: 'PROPERTY.Default_Value',
        type: 'text',
        size: 20,
        onChange: () => this.onChange()
      }
    ],
    [PropertyCategory.InService]: [
      {
        name: 'value',
        title: 'PROPERTY.Default_Value',
        type: 'radio',
        size: 20,
        options: [
          { id: '2', description: 'PROPERTY.State_Up' },
          { id: '1', description: 'PROPERTY.State_Strained' },
          { id: '0', description: 'PROPERTY.State_Down' },
        ],
        onChange: () => this.onChange()
      }
    ],
    [PropertyCategory.InUse]: [
      {
        name: 'value',
        title: 'PROPERTY.Default_Value',
        type: 'radio',
        size: 20,
        options: [
          { id: '1', description: 'PROPERTY.State_InUse' },
          { id: '0', description: 'PROPERTY.State_NotInUse' },
        ],
        onChange: () => this.onChange()
      }
    ],
    [PropertyCategory.Timestamp]: [
      {
        name: 'value',
        type: 'customElement',
        required: true,
        component: DateRangePickerComponent,
        inputs: {
          singleDatePicker: true,
          isTimePicker: true,
          emptyDefaultValue: true,
          useUTC: true,
          startLabel: 'PROPERTY.Default_Value'
        },
        onChange: () => this.onChange()
      }
    ],
    [PropertyCategory.Zone]: [
      {
        title: 'SHARED.Location',
        name: 'location',
        type: 'selectmenu',
        required: true,
        multiple: false,
        canClear: false,
        placeholder: this.translate.instant('SHARED.Any_Location'),
        valueProperty: 'locationID',
        options: this.userService.getUserLocations(this.userdataService.locations, false),
        func: (ref: ILocation) => ref?.name,
        test: (ref: ILocation) => !get(ref, 'disabledAt'),
        onChange: (ids) => this.onLocationChange(ids)
      },
      {
        title: 'SHARED.Zone',
        name: 'zone',
        required: true,
        type: 'selectmenu',
        placeholder: this.translate.instant('SHARED.Any_Zone'),
        options: this.zoneService.getGroupedZonesByLocations(this.userdataService.locations),
        onChange: () => this.onChange()
      },
    ]
  };

  protected formConfig = {
    containers: true,
    prefix: this.formPrefix,
    fields: []
  };

  constructor(
    private formBuilderService: FormBuilderService,
    private userService: UserService,
    private userdataService: UserdataService,
    private zoneService: ZoneService,
    private translate: TranslateService
  ) {
    super();
  }

  ngAfterViewInit() {
    this.applyCategory();
    this.checkFieldsVisibility();
  }

  public applyCategory() {
    const fields = this.getFields();

    if (fields?.length) {
      this.formConfig.fields = fields;
      this.formBuilderService.showForm(`#${this.formId}`, this.formConfig, this.getValue());
      this.onChange();
    }
  }

  private getFields(): FormField[] {
    const fields = cloneDeep(this.fields[this.propertyCategory]);
    const isInService = this.propertyCategory === PropertyCategory.InService;
    const isUpDown = this.propertyStyle === PropertyStyle.UpDown;
    const isSourceValid = includes([PropertySource.Manual, PropertySource.Hybrid], this.propertySource);

    if (isInService && isUpDown && isSourceValid) {
      const valueField = find(fields, { name: 'value' });

      valueField.options = reject(valueField.options, (option) => {
        return +option.id === 1;
      });
    }

    return fields;
  }

  private getValue() {
    if (this.propertyCategory === PropertyCategory.Zone) {
      let [location, zone] = split(this.formValue, ':');

      if (+zone === 0) {
        zone = `${location}:${zone}`;
      }

      return location && zone ? {zone, location} : {};
    } else if (this.propertyCategory === PropertyCategory.Timestamp) {
      return this.formValue && moment(+this.formValue) ? { value: +this.formValue } : {};
    } else {
      return this.formValue ? { value: this.formValue } : {};
    }
  }

  public syncFieldVisibility() {
    if (!this.formValue) {
      each(this.formConfig.fields, (field) => {
        this.formBuilderService.hideFieldsOnValue(field, this.formConfig, {});
      });
    }
  }

  public isValid(): boolean {
    return ($(`#${this.formId}`) as any).valid();
  }

  private checkFieldsVisibility() {
    if (this.propertyCategory === PropertyCategory.Zone) {
      const [location] = split(this.formValue, ':');

      if (location) {
        this.onLocationChange(location);
      }
    }
  }

  private onChange() {
    setTimeout(() => {
      const formData = this.formBuilderService.getFormData($(`#${this.formId}`));

      if (this.propertyCategory === PropertyCategory.Zone) {
        if (formData.location && formData.zone) {
          const zone = last(split(formData.zone, ':')) ?? formData.zone;
          this.formValue = `${formData.location}:${zone}`;
        }
      } else {
        this.formValue = formData?.value;
      }
    });
  }

  private onLocationChange(locationId: string) {
    const formId = `#${this.formPrefix}zone`;

    const zone: string | number = <string | number>$(formId).val();
    const fieldOptions: FormField = Object.assign(find(this.formConfig.fields, {name: 'zone'}), {
      options: this.zoneService.getGroupedZonesByLocations([+locationId])
    });
    this.formBuilderService.replaceSelectOptionsWith(formId, this.formConfig, fieldOptions, { zone });

    this.onChange();
  }
}
